// Owns a correlation-finder partial's state: the mode its chart is drawn in, how the pair is laid
// out, and whether the legend is up. Seeded from the params the partial rides on and re-seeded
// whenever any of them moves outside the block.
//
// The state was split across two coffee views -- the block read the layout, the chart view read the
// mode and wrote both down. It is one bag here, and the block answers the exporters for all of it,
// because the partial view hosting the block is what they reach these bridges through.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isEqual } from 'lodash'

import { getModelValue } from '@aytm/app/stats/lib/stat-page'
import {
  calcDisplayRange,
  hasDisplayRange
} from '@aytm/app/stats/page/question-views/cq/components/partial/lib/display-range'
import { getDefaultChartMode } from '@aytm/app/stats/page/question-views/cq/components/partial/lib/chartModes'
import {
  calcParamsSection,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/cq/components/partial/lib/report-params'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import useParamsReseed, { isUnchanged } from '@aytm/app/stats/page/question-views/components/hooks/useParamsReseed'

export default function usePartialState({
  params,
  paramsRevision,
  questionKey,
  format,
  headers,
  batches,
  answer,
  answerType,
  ignoreAmount,
  ignorePercents,
  questionChartMode,
  mode,
  onReady,
  onStateChange
}) {
  const contextRef = useRef()
  contextRef.current = {
    key: questionKey,
    format,
    headers,
    batches,
    answer,
    answerType,
    ignoreAmount,
    ignorePercents,
    questionChartMode
  }

  const [state, setState] = useState(() =>
    resolveReportParams({ ...contextRef.current, params: params || {}, globalChartMode: getModelValue('chartMode') })
  )

  const snapshot = useRef()
  snapshot.current = state

  // the mounted chart knows the axis it is showing; the exporters ask the state for it
  const chartApiRef = useRef(null)
  const onChartReady = useCallback(api => {
    chartApiRef.current = api
  }, [])

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}, reason = 'state') =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = reason

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(() => {
    // the mode the chart opens on once the question and global defaults are applied -- what the
    // exporters draw by, as opposed to `chartMode`, which is null until the researcher picks one
    const resolveExportChartMode = () => {
      const { batches, ignorePercents, questionChartMode } = contextRef.current
      const { chartMode, groupType } = snapshot.current

      return getDefaultChartMode({
        chartMode,
        questionChartMode,
        globalChartMode: getModelValue('chartMode'),
        batches,
        ignorePercents,
        groupType
      })
    }

    // the axis the chart is showing. The rendered chart knows it, zoom included; the maths is the
    // fallback for a chart that has not drawn yet.
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const context = contextRef.current
      const { axisType } = snapshot.current

      const globalChartMode = getModelValue('chartMode')
      if (!hasDisplayRange(chartMode, globalChartMode)) return null

      const extremes = chartApiRef.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        zoomLevel: zoomLevel || snapshot.current.zoomLevel || getModelValue('zoomLevel'),
        globalChartMode,
        format: context.format,
        answer: context.answer,
        batches: context.batches,
        axisType,
        answerType: context.answerType,
        ignoreAmount: context.ignoreAmount,
        ignorePercents: context.ignorePercents,
        confidenceLevels: PageGlobals.CONFIDENCE_LEVELS
      })
    }

    return {
      getState: () => ({ ...snapshot.current }),
      getReportParamsSection: () => {
        const { batches, ignorePercents, questionChartMode } = contextRef.current

        return calcParamsSection({
          ...snapshot.current,
          batches,
          ignorePercents,
          questionChartMode,
          globalChartMode: getModelValue('chartMode'),
          globalZoomLevel: getModelValue('zoomLevel')
        })
      },
      // pptx / summary bridge: what the partial is showing -- the mode it is drawn in and the axis
      // that goes with it
      getExportData: () => {
        const { chartMode, groupType, axisType } = snapshot.current
        const chartModeResolved = resolveExportChartMode()

        return {
          chartMode,
          chartModeResolved,
          displayRange: resolveDisplayRange(chartModeResolved, null),
          groupType,
          axisType
        }
      },
      getDisplayRange: resolveDisplayRange,
      update: onModelChange
    }
  }, [onModelChange])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  useParamsReseed(params ?? {}, paramsRevision, (source, previous) => {
    setState(current => {
      const next = {
        ...resolveReportParams({ ...contextRef.current, params: source, globalChartMode: getModelValue('chartMode') }),
        // a legend describes the pair it was opened over: only that pair moving closes it, not a
        // mode or a zoom
        legendOpen: isEqual(previous?.cf, source.cf) ? current.legendOpen : false
      }
      if (isUnchanged(current, next)) return current
      pendingReason.current = 'seed'
      return next
    })
  })

  // The legend explains one of the two statistics; switching between them leaves it describing
  // something that is no longer on screen, so it closes.
  const lastMode = useRef(mode)
  useEffect(() => {
    if (lastMode.current === mode) return
    lastMode.current = mode

    setState(current => (current.legendOpen ? { ...current, legendOpen: false } : current))
  }, [mode])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  const onChartModeChange = useCallback(chartMode => onModelChange({ chartMode }), [onModelChange])

  // the chart calls it with its own key, which the block already knows
  const onZoomChange = useCallback((key, zoomLevel) => onModelChange({ zoomLevel }), [onModelChange])

  const onGroupTypeChange = useCallback(groupType => onModelChange({ groupType }), [onModelChange])

  const onAxisTypeChange = useCallback(axisType => onModelChange({ axisType }), [onModelChange])

  // the legend is not written down, so opening it announces nothing for the shell to store
  const onToggleLegend = useCallback(
    () => onModelChange({ legendOpen: !snapshot.current.legendOpen }, 'legend'),
    [onModelChange]
  )

  return {
    ...state,
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onGroupTypeChange,
    onAxisTypeChange,
    onToggleLegend
  }
}
